'use client';

import { useState } from 'react';
import Link from 'next/link';
import type { ComponentProps, MouseEvent, Ref } from 'react';

type HoverPrefetchLinkProps = ComponentProps<typeof Link>;

/**
 * Link that prefetches on hover instead of on viewport entry.
 *
 * Next.js defaults to prefetching every Link that enters the viewport,
 * which can generate dozens of RSC requests on pages with many navigation
 * links. This wrapper disables viewport prefetching and restores default
 * (static) prefetching once the user hovers — still instant for users
 * but dramatically reduces background bandwidth.
 *
 * @see https://nextjs.org/docs/app/api-reference/components/link#prefetch
 *
 * Forwards `ref` to the underlying anchor so it can be used as a Radix
 * `asChild` target (e.g. wrapped in a `Tooltip`), which merges its own ref
 * onto the immediate child.
 */
export function HoverPrefetchLink({
  onMouseEnter,
  ref,
  ...props
}: HoverPrefetchLinkProps & { ref?: Ref<HTMLAnchorElement> }) {
  const [active, setActive] = useState(false);

  return (
    <Link
      {...props}
      ref={ref}
      prefetch={active ? null : false}
      onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
        setActive(true);
        onMouseEnter?.(e);
      }}
    />
  );
}
